import { resolveInAppPathFromUrl } from '~/utils/universalLink'

let listenersRegistered = false

const COLD_START_RETRY_MS = [500, 1500, 3000] as const

export default defineNuxtPlugin({
  name: 'universal-links',
  enforce: 'post',
  async setup(nuxtApp) {
    if (!import.meta.client) {
      return
    }

    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) {
      return
    }

    const { App } = await import('@capacitor/app')
    const router = useRouter()

    let pendingUrl: string | null = null
    let readyToNavigate = false
    let flushInProgress = false

    function pathsMatch(current: string, target: string): boolean {
      const normalize = (p: string) => p.replace(/\/+$/, '') || '/'
      return normalize(current) === normalize(target)
        || normalize(current).startsWith(`${normalize(target)}/`)
    }

    async function navigateToPath(path: string): Promise<boolean> {
      await router.isReady()
      await navigateTo(path, { replace: true })
      await nextTick()
      return pathsMatch(router.currentRoute.value.fullPath, path)
    }

    async function flushPendingUrl(): Promise<void> {
      if (!readyToNavigate || !pendingUrl || flushInProgress) {
        return
      }

      const url = pendingUrl
      flushInProgress = true

      try {
        const path = resolveInAppPathFromUrl(url)
        if (!path) {
          return
        }

        const matched = await navigateToPath(path)
        // Only clear when we actually landed on the deep-link target
        if (matched && pendingUrl === url) {
          pendingUrl = null
        }
      }
      catch (error) {
        console.warn('Failed to handle universal link:', error)
      }
      finally {
        flushInProgress = false
      }
    }

    function queueUrl(url: string): void {
      pendingUrl = url
      void flushPendingUrl()
    }

    async function pollLaunchUrl(): Promise<void> {
      if (pendingUrl) {
        return
      }

      try {
        const launch = await App.getLaunchUrl()
        if (launch?.url) {
          pendingUrl = launch.url
        }
      }
      catch (error) {
        console.warn('Failed to read launch URL:', error)
      }
    }

    async function markReadyAndFlush(): Promise<void> {
      await router.isReady()
      readyToNavigate = true
      await flushPendingUrl()
    }

    async function registerListeners(): Promise<void> {
      if (listenersRegistered) {
        return
      }

      listenersRegistered = true

      await App.addListener('appUrlOpen', (event) => {
        if (event.url) {
          queueUrl(event.url)
        }
      })
    }

    await registerListeners()
    await pollLaunchUrl()

    // app:mounted may already have fired (async plugin setup) — still flush after router ready
    nuxtApp.hook('app:mounted', () => {
      void markReadyAndFlush()
    })

    // Re-apply if Nuxt finished a page nav back to home before deep link stuck
    nuxtApp.hook('page:finish', () => {
      if (pendingUrl) {
        void flushPendingUrl()
      }
    })

    await markReadyAndFlush()

    // Cold start: launch URL / hydration may arrive after first flush
    for (const delayMs of COLD_START_RETRY_MS) {
      window.setTimeout(() => {
        void (async () => {
          await pollLaunchUrl()
          await flushPendingUrl()
        })()
      }, delayMs)
    }
  },
})
